import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import clsx from 'clsx';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import queryString from 'query-string';
import Alert from '@material-ui/lab/Alert';
import Typography from '@material-ui/core/Typography';
import moment from 'moment-timezone';

import { colors, moodColors } from 'styles';
import EmptyTrunk from 'components/animations/EmptyTrunk';
import ActivityList from 'components/ActivityList';
import Dropdown from 'components/Dropdown';
import Loading from 'components/animations/Loading';
import useSenslaApi from 'hooks/useSenslaApi';
import WeekDaysChart from 'components/charts/WeekDaysChart';
import HeatmapChart from 'components/charts/HeatmapChart';

import { ctx } from 'store';

import { useStyles } from './styles';

// Counters
import ChannelsCounter from '../counters/ChannelsCounter';
import ActivityCounter from '../counters/ActivityCounter';
import ActiveMembersCounter from '../counters/ActiveMembersCounter';
import MoodCounter from '../counters/MoodCounter';
import MentionsCounter from '../counters/MentionsCounter';

interface IPropsFill {
  value: number;
  seriesIndex?: string;
  w?: any;
}

const hours = [
  0,
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  11,
  12,
  13,
  14,
  15,
  16,
  17,
  18,
  19,
  20,
  21,
  22,
  23,
];
const Reports: React.FC = () => {
  const {
    teams: { currentTeam },
  } = useContext(ctx.store);
  const location = useLocation();
  const classes = useStyles();
  const history = useHistory();
  const [selectedChannel, setSelectedChannel] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [daysSelected, setDaysSelected] = useState(7);
  const [channels, setChannels] = useState<{ id: string }[]>([]);
  const [groups, setGroups] = useState<{ id: string }[]>([]);
  const queryParams: any = queryString.parse(location.search);
  const { channelId, groupId } = queryParams;
  const [channel, setChannel] = useState(channelId ? channelId : 'All-channels');
  const [group, setGroup] = useState(groupId ? groupId : 'All-teams');

  function activityByWeek(e: any) {
    const weekDay = activityReport.daysActivity.filter((ac: any) => ac.dayOfWeek === e);
    if (weekDay.length > 0) {
      return weekDay[0];
    } else {
      return { _id: e, dayOfWeek: e, count: 0, avg: 0, total: 0 };
    }
  }

  function previousActivityByWeek(e: any) {
    const weekDay = activityReport.previousDaysActivity.filter((ac: any) => ac.dayOfWeek === e);
    if (weekDay.length > 0) {
      return weekDay[0];
    } else {
      return { _id: e, dayOfWeek: e, count: 0, avg: 0, total: 0 };
    }
  }

  const query = queryString.stringify(
    {
      days: daysSelected,
      channel: channel === 'All-channels' ? null : channel,
      group: group === 'All-teams' ? null : group,
      team: currentTeam.teamId ? currentTeam.teamId : currentTeam.tenantId,
      platform: currentTeam.teamId ? 'slack' : 'microsoft',
    },
    {
      skipNull: true,
    }
  );

  const [{ data: activityReport, loading }] = useSenslaApi(
    `activities/report/?${query}`,
    'GET',
    true
  );

  useEffect(() => {
    const data = [
      ...(currentTeam.teamId ? currentTeam.channels : currentTeam.msChannels).map((ch: any) => {
        return { id: ch.channelId };
      }),
    ];
    const groupsData = [
      ...(currentTeam.teamId ? [] : currentTeam.msGroups)
        .filter((gr: any) => gr.monitored)
        .map((group: any) => {
          return { id: group.groupId };
        }),
    ];
    onChange(channelId, 'channels', true);
    setSelectedChannel(channelId);
    setChannels(data);
    if (!currentTeam.teamId) {
      onChange(groupId, 'teams', true);
      setSelectedGroup(groupId);
      setGroups(groupsData);
    }
    setDaysSelected(7);
  }, []);

  const mostActiveMembers = useMemo(() => {
    if (!activityReport) return [];
    return activityReport.memberActivity
      .sort((m1: any, m2: any) => m2.count - m1.count)
      .filter(
        (mem: any) =>
          mem.member &&
          !mem.member.isBot &&
          !mem.member.isRestricted &&
          !mem.member.deleted &&
          mem.member.memberId !== 'USLACKBOT'
      )
      .map((m: any) => ({
        member: m.member.memberId,
        username: m.member.username,
        count: m.count,
        score: m.avg,
      }));
  }, [activityReport]);

  function* filterA(array: any, condition: any, maxSize: any) {
    if (!maxSize || maxSize > array.length) {
      maxSize = array.length;
    }
    let count = 0;
    let i = 0;
    while (count < maxSize && i < array.length) {
      if (condition(array[i])) {
        yield array[i];
        count++;
      }
      i++;
    }
  }

  const membersWithoutActivity = useMemo(() => {
    if (!activityReport) return [];
    const activeMembers = activityReport.memberActivity
      .filter((mem: any) => mem.member)
      .map((m: any) => m.member.memberId);
    return Array.from(
      filterA(
        currentTeam.teamId ? currentTeam.members : currentTeam.msMembers,
        (m: any) =>
          !activeMembers.includes(m.memberId) &&
          !m.isBot &&
          !m.isRestricted &&
          !m.deleted &&
          m.memberId !== 'USLACKBOT' &&
          (channel === ''
            ? true
            : (currentTeam.teamId ? currentTeam.channels : currentTeam.msChannels)
                .filter((ch: any) => ch.channelId === channel)[0]
                .members.includes(m.memberId)),
        50
      )
    ).map((m: any) => ({ member: m.memberId, username: m.username }));
  }, [activityReport]);

  const mostProblematicMembers = useMemo(() => {
    if (!activityReport) return [];
    return activityReport.memberLowMood
      .filter(
        (mem: any) =>
          mem.member &&
          !mem.member.isBot &&
          !mem.member.isRestricted &&
          !mem.member.deleted &&
          mem.member.count > 1 &&
          mem.member.memberId !== 'USLACKBOT'
      )
      .map((m: any) => ({
        member: m.member.memberId,
        username: m.member.username,
        score: m.avg,
        count: m.count,
      }));
  }, [activityReport]);

  const daysActivity = useMemo(() => {
    if (!activityReport) return [];
    const activity = [1, 2, 3, 4, 5, 6, 7].map(activityByWeek);
    return activity.map((d: any) => d.count);
  }, [activityReport]);

  const previousDaysActivity = useMemo(() => {
    if (!activityReport) return [];
    const activity = [1, 2, 3, 4, 5, 6, 7].map(previousActivityByWeek);
    return activity.map((d: any) => d.count);
  }, [activityReport]);

  const heatHours = useMemo(() => {
    if (!activityReport) return {};
    const heatActivity: { name: string; data: number[] } | any = [];
    const heatMood: { name: string; data: number[] } | any = [];
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Expensive operation
    const finalMapping: any = {};
    for (const day of activityReport.hoursActivity) {
      const dayNumber = day._id.day;
      for (const hour of day.hours) {
        const time = moment.utc(`${dayNumber - 1} ${hour.hour}`, 'd kk').tz(moment.tz.guess());
        const tzHour = { ...hour, hour: time.hour() };
        if (finalMapping[time.day() + 1]) {
          finalMapping[time.day() + 1].push(tzHour);
        } else {
          finalMapping[time.day() + 1] = [tzHour];
        }
      }
    }

    [1, 2, 3, 4, 5, 6, 7].forEach((dayNumber: any) => {
      const filteredDay = finalMapping[dayNumber];
      if (filteredDay) {
        const day = filteredDay;
        heatMood.push({
          name: days[dayNumber - 1],
          data: hours.map((h: any) => {
            const filteredHours = day.filter((ho: any) => ho.hour === h);
            if (filteredHours.length > 0) {
              return filteredHours[0].avg.toFixed(2);
            } else {
              return 0;
            }
          }),
        });
        heatActivity.push({
          name: days[dayNumber - 1],
          data: hours.map((h: any) => {
            const filteredHours = day.filter((ho: any) => ho.hour === h);
            if (filteredHours.length > 0) {
              return filteredHours[0].count;
            } else {
              return 0;
            }
          }),
        });
      } else {
        heatMood.push({
          name: days[dayNumber - 1],
          data: Array(24).fill(0),
        });
        heatActivity.push({
          name: days[dayNumber - 1],
          data: Array(24).fill(0),
        });
      }
    });
    return {
      heatMood,
      heatActivity,
    };
  }, [activityReport]);

  const daysProblematic = useMemo(() => {
    if (!activityReport) return [];
    const activity = [1, 2, 3, 4, 5, 6, 7].map(activityByWeek);
    return activity.map((d: any) => d.avg);
  }, [activityReport]);

  const previousDaysProblematic = useMemo(() => {
    if (!activityReport) return [];
    const activity = [1, 2, 3, 4, 5, 6, 7].map(previousActivityByWeek);
    return activity.map((d: any) => d.avg);
  }, [activityReport]);

  // Counters
  const onChange = (value: string, type: string, fromEffect?: boolean) => {
    if (!fromEffect) {
      let groupQuery =
        group || type === 'teams' ? `groupId=${type === 'teams' ? value : group}` : '';
      let channelQuery =
        channel || type === 'channels' ? `channelId=${type === 'channels' ? value : channel}` : '';

      groupQuery = groupQuery === 'groupId=All-teams' ? '' : groupQuery;
      channelQuery = channelQuery === 'channelId=All-channels' ? '' : channelQuery;

      let completeQuery;
      if (groupQuery !== '' && channelQuery !== '') {
        completeQuery = `?${groupQuery}&${channelQuery}`;
      } else if (groupQuery === '') {
        completeQuery = `?${channelQuery}`;
      } else if (channelQuery === '') {
        completeQuery = `?${groupQuery}`;
      }

      history.push(`/dashboard/reports${completeQuery}`);
    }

    if (type === 'channels') {
      setSelectedChannel(value);
      const filteredChannel = (currentTeam.teamId
        ? currentTeam.channels
        : currentTeam.msChannels
      ).filter((channel: any) => channel.channelId === value)[0];
      if (filteredChannel) {
        setChannel(filteredChannel.channelId);
      } else {
        setChannel('');
      }
    } else if (type === 'teams') {
      setSelectedGroup(value);
      const filteredGroup = currentTeam.msGroups.filter((group: any) => group.groupId === value)[0];
      if (filteredGroup) {
        setGroup(filteredGroup.groupId);
      } else {
        setGroup('');
      }
    }
  };

  const onChangeDate = (value: any) => {
    setDaysSelected(value);
  };

  const dropdownChannels = useMemo(() => {
    if (!groupId || groupId === '') {
      return channels;
    }
    return currentTeam.msGroups
      .find((group: any) => group.groupId === groupId)
      .channels.map((channel: any) => ({ id: channel }));
  }, [groupId, channels]);

  return (
    <div style={{ height: '100vh' }}>
      <Box display="flex" p={1} alignItems="center" className={classes.header}>
        <Box p={1} flexGrow={1}>
          <Typography component="h1" variant="h5">
            Reports
          </Typography>
        </Box>
        <Box p={1} flexShrink={0} className={classes.channelSelect}>
          <Dropdown
            data={[{ id: 7 }, { id: 14 }, { id: 30 }, { id: 90 }]}
            translate={'days'}
            type="days"
            setValue={onChangeDate}
            value={String(daysSelected)}
            defaultValue={String(daysSelected)}
          />
        </Box>
        {!currentTeam.teamId ? (
          <Box p={1} flexShrink={0} className={classes.channelSelect}>
            <Dropdown
              addWorkspace
              data={groups}
              translate={currentTeam.msGroups}
              type="teams"
              setValue={onChange}
              value={selectedGroup}
              autocomplete
              defaultValue={groupId}
            />
          </Box>
        ) : null}
        <Box p={1} flexShrink={0} className={classes.channelSelect}>
          <Dropdown
            addWorkspace
            data={dropdownChannels}
            translate={currentTeam.teamId ? currentTeam.channels : currentTeam.msChannels}
            type="channels"
            setValue={onChange}
            value={selectedChannel}
            autocomplete
            defaultValue={channelId}
          />
        </Box>
      </Box>
      {!loading ? (
        <>
          {activityReport &&
          activityReport.channelActivity &&
          activityReport.channelActivity.length === 0 ? (
            <div className={classes.noData}>
              <Typography component="h2">{`No data to be displayed in the last ${daysSelected} days!`}</Typography>
              <EmptyTrunk animationWidth={400} animationHeight={400} containerHeight="initial" />
            </div>
          ) : (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={2}>
                  <ActivityCounter activityReport={activityReport} />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <ChannelsCounter activityReport={activityReport} currentTeam={currentTeam} />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <ActiveMembersCounter
                    activityReport={activityReport}
                    currentTeam={currentTeam}
                    channel={channel}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <MentionsCounter activityReport={activityReport} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <MoodCounter activityReport={activityReport} />
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper className={clsx(classes.paper, classes.metricBoxChart)}>
                    <Typography component="h2" className={classes.subtitle}>
                      Daily activity
                    </Typography>

                    <WeekDaysChart
                      previousDaysValues={previousDaysActivity}
                      daysValues={daysActivity}
                      options={{
                        yaxis: {
                          title: {
                            text: 'Activity',
                          },
                          labels: {
                            formatter: function (val: number) {
                              if (Number.isInteger(val)) {
                                return val;
                              }
                            },
                          },
                        },
                      }}
                      chartType="area"
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper className={clsx(classes.paper, classes.metricBoxChart)}>
                    <Typography component="h2" className={classes.subtitle}>
                      Average daily mood
                    </Typography>
                    <WeekDaysChart
                      previousDaysValues={previousDaysProblematic}
                      daysValues={daysProblematic}
                      options={{
                        yaxis: {
                          title: {
                            text: 'Mood',
                          },
                          axisBorder: {
                            show: false,
                          },
                          labels: {
                            show: false,
                          },
                          axisTicks: {
                            show: false,
                          },
                          max: 1,
                          tickAmount: 4,
                        },
                        stroke: {
                          show: true,
                          colors: '#fff',
                          width: 2,
                        },
                        fill: {
                          type: ['solid', 'pattern'],
                          colors: [
                            function ({ value }: IPropsFill) {
                              if (value < 0.333) {
                                return moodColors.negative;
                              } else if (value >= 0.333 && value < 0.666) {
                                return moodColors.neutral;
                              } else {
                                return moodColors.positive;
                              }
                            },
                          ],
                          pattern: {
                            style: 'slantedLines',
                            strokeWidth: 3,
                          },
                        },
                      }}
                    />
                  </Paper>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper className={clsx(classes.paper, classes.metricBoxChart)}>
                    <Typography component="h2" className={classes.subtitle}>
                      Activity Heatmap
                    </Typography>
                    {heatHours.heatActivity && (
                      <HeatmapChart
                        data={heatHours.heatActivity}
                        options={{
                          colors: [colors.orangeBrand],
                          plotOptions: {
                            heatmap: {
                              distributed: true,
                            },
                          },
                        }}
                      />
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper className={clsx(classes.paper, classes.metricBoxChart)}>
                    <Typography component="h2" className={classes.subtitle}>
                      Mood Heatmap
                    </Typography>
                    {heatHours.heatMood && (
                      <HeatmapChart
                        data={heatHours.heatMood}
                        options={{
                          plotOptions: {
                            heatmap: {
                              shadeIntensity: 0.5,
                              radius: 2,
                              colorScale: {
                                ranges: [
                                  {
                                    from: 0.00000000011,
                                    to: 0.333,
                                    name: 'negative',
                                    color: moodColors.negative,
                                  },
                                  {
                                    from: 0.334,
                                    to: 0.666,
                                    name: 'neutral',
                                    color: moodColors.neutral,
                                  },
                                  {
                                    from: 0.667,
                                    to: 1,
                                    name: 'positive',
                                    color: moodColors.positive,
                                  },
                                  {
                                    from: 0.0,
                                    to: 0.0000000001,
                                    name: 'none',
                                    color: moodColors.default,
                                  },
                                ],
                              },
                            },
                          },
                        }}
                      />
                    )}
                  </Paper>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Paper className={clsx(classes.paper, classes.metricBoxChart)}>
                    <Typography component="h2" className={classes.subtitle}>
                      Most engaged users
                    </Typography>
                    <Typography component="h3" className={classes.subtitlePd}>
                      Top 100
                    </Typography>
                    <div className={classes.listView}>
                      {mostActiveMembers.map((member: any, index: number) => {
                        return (
                          <ActivityList
                            data={member}
                            translate={
                              currentTeam.teamId ? currentTeam.members : currentTeam.msMembers
                            }
                            type="memberActivity"
                            key={index}
                          />
                        );
                      })}
                    </div>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper className={clsx(classes.paper, classes.metricBoxChart)}>
                    <Typography component="h2" className={classes.subtitle}>
                      Members without Activity
                    </Typography>
                    <Typography component="h3" className={classes.subtitlePd}>
                      Max sample of 50
                    </Typography>
                    <div
                      className={classes.listView}
                      style={
                        !membersWithoutActivity.length
                          ? { display: 'flex', justifyContent: 'center' }
                          : {}
                      }
                    >
                      {membersWithoutActivity.length ? (
                        membersWithoutActivity.map((member: any, index: number) => (
                          <ActivityList
                            data={member}
                            translate={
                              currentTeam.teamId ? currentTeam.members : currentTeam.msMembers
                            }
                            type="memberActivity"
                            hideMood={true}
                            key={index}
                          />
                        ))
                      ) : (
                        <Alert severity="success">
                          Congrats! All workspace members have been active! WELL DONE!
                        </Alert>
                      )}
                    </div>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper className={clsx(classes.paper, classes.metricBoxChart)}>
                    <Typography component="h2" className={classes.subtitle}>
                      Low mood avg
                    </Typography>
                    <Typography component="h3" className={classes.subtitlePd}>
                      Bottom 100
                    </Typography>
                    <div
                      className={classes.listView}
                      style={
                        !mostProblematicMembers.filter((mem: any) => mem.score < 0.333).length
                          ? { display: 'flex', justifyContent: 'center' }
                          : {}
                      }
                    >
                      {mostProblematicMembers.filter((mem: any) => mem.score < 0.333).length ? (
                        mostProblematicMembers
                          .filter((mem: any) => mem.score < 0.333)
                          .map((member: any, index: number) => (
                            <ActivityList
                              data={member}
                              translate={
                                currentTeam.teamId ? currentTeam.members : currentTeam.msMembers
                              }
                              type="memberActivity"
                              key={index}
                            />
                          ))
                      ) : (
                        <Alert severity="success">No team member showed low mood. EXCELLENT!</Alert>
                      )}
                    </div>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </>
      ) : (
        <div className={classes.loading}>
          <Loading containerHeight="70vh" />
        </div>
      )}
    </div>
  );
};

export { Reports };
